/** All screener filter keys (dropdown values are min thresholds unless sector/industry). */
export type ScreenerFilterFieldKey =
  | "sector"
  | "industry"
  | "market_cap"
  | "volume"
  | "avg_volume"
  | "shares_out"
  | "float"
  | "price"
  | "halal_rating"
  | "akinda_ranking"
  | "profitability"
  | "liquidity"
  | "financial_strength"
  | "value"
  | "growth"
  | "momentum"
  | "pe"
  | "p_cash"
  | "roe"
  | "quick_ratio"
  | "impure_ratio"
  | "forward_pe"
  | "p_free_cash_flow"
  | "roa"
  | "current_ratio"
  | "peg"
  | "dividend_yield"
  | "gross_margin"
  | "payout_ratio"
  | "ps"
  | "eps_growth_this_year"
  | "profit_margin"
  | "debt_ratio"
  | "pb"
  | "eps_growth_next_year"
  | "oper_margin"
  | "liquidity_ratio"
  | "rsi_14"
  | "beta"
  | "change_perc"
  | "gap_perc"
  | "relative_volume"
  | "perf_week"
  | "perf_month"
  | "perf_ytd"
  | "perf_year";

export type ScreenerFilterValues = Record<ScreenerFilterFieldKey, string> & {
  halalOnly: boolean;
};

const emptyStringFields = (): Record<ScreenerFilterFieldKey, string> => ({
  sector: "",
  industry: "",
  market_cap: "",
  volume: "",
  avg_volume: "",
  shares_out: "",
  float: "",
  price: "",
  halal_rating: "",
  akinda_ranking: "",
  profitability: "",
  liquidity: "",
  financial_strength: "",
  value: "",
  growth: "",
  momentum: "",
  pe: "",
  p_cash: "",
  roe: "",
  quick_ratio: "",
  impure_ratio: "",
  forward_pe: "",
  p_free_cash_flow: "",
  roa: "",
  current_ratio: "",
  peg: "",
  dividend_yield: "",
  gross_margin: "",
  payout_ratio: "",
  ps: "",
  eps_growth_this_year: "",
  profit_margin: "",
  debt_ratio: "",
  pb: "",
  eps_growth_next_year: "",
  oper_margin: "",
  liquidity_ratio: "",
  rsi_14: "",
  beta: "",
  change_perc: "",
  gap_perc: "",
  relative_volume: "",
  perf_week: "",
  perf_month: "",
  perf_ytd: "",
  perf_year: "",
});

export const EMPTY_FILTERS: ScreenerFilterValues = {
  ...emptyStringFields(),
  halalOnly: false,
};

export const DESCRIPTIVE_FILTER_FIELDS: ScreenerFilterFieldKey[] = [
  "sector",
  "industry",
  "market_cap",
  "volume",
  "avg_volume",
  "shares_out",
  "float",
  "price",
  "halal_rating",
];

export const RANKING_FILTER_FIELDS: ScreenerFilterFieldKey[] = [
  "akinda_ranking",
  "profitability",
  "liquidity",
  "financial_strength",
  "value",
  "growth",
  "momentum",
];

/** Reference Fundamental tab (5×4 grid). */
export const FUNDAMENTAL_FILTER_FIELDS: ScreenerFilterFieldKey[] = [
  "pe",
  "p_cash",
  "roe",
  "quick_ratio",
  "impure_ratio",
  "forward_pe",
  "p_free_cash_flow",
  "roa",
  "current_ratio",
  "peg",
  "dividend_yield",
  "gross_margin",
  "payout_ratio",
  "ps",
  "eps_growth_this_year",
  "profit_margin",
  "debt_ratio",
  "pb",
  "eps_growth_next_year",
  "oper_margin",
  "liquidity_ratio",
];

/** Reference Technical tab. */
export const TECHNICAL_FILTER_FIELDS: ScreenerFilterFieldKey[] = [
  "rsi_14",
  "beta",
  "change_perc",
  "gap_perc",
  "relative_volume",
  "perf_week",
  "perf_month",
  "perf_ytd",
  "perf_year",
];

export const ALL_FILTER_FIELDS: ScreenerFilterFieldKey[] = [
  ...new Set<ScreenerFilterFieldKey>([
    ...DESCRIPTIVE_FILTER_FIELDS,
    ...RANKING_FILTER_FIELDS,
    ...FUNDAMENTAL_FILTER_FIELDS,
    ...TECHNICAL_FILTER_FIELDS,
  ]),
];

export type ScreenerFilterTab = "descriptive" | "fundamental" | "technical" | "all";

export const FILTER_FIELDS_BY_TAB: Record<
  ScreenerFilterTab,
  ScreenerFilterFieldKey[]
> = {
  descriptive: [...DESCRIPTIVE_FILTER_FIELDS, ...RANKING_FILTER_FIELDS],
  fundamental: FUNDAMENTAL_FILTER_FIELDS,
  technical: TECHNICAL_FILTER_FIELDS,
  all: ALL_FILTER_FIELDS,
};
